import { Component, forwardRef } from 'react';
import * as PropTypes from 'prop-types';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import classnames from 'classnames/bind';
import { GoCalendar as Calendar } from 'react-icons/go';
import { MdClear as ResetIcon } from 'react-icons/md';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import enGB from 'date-fns/locale/en-GB';
import Input from '../Input';
import MaskedInput from '../MaskedInput';
import Button from '../Button';
import styles from './DateTimeInput.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
registerLocale('en-GB', enGB);
setDefaultLocale('en-GB');
const cln = classnames.bind(styles);
const IconOnlyButton = /*#__PURE__*/forwardRef(({
  onClick,
  icon
}, ref) => /*#__PURE__*/_jsx(Button, {
  ref: ref,
  type: "button",
  variant: "standard",
  iconOnly: true,
  onClick: onClick,
  children: icon
}));
IconOnlyButton.displayName = 'IconOnlyButton';
IconOnlyButton.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.node
};
IconOnlyButton.defaultProps = {
  onClick: undefined,
  icon: /*#__PURE__*/_jsx(Calendar, {})
};
/**
 * This component is a wrapper on [react-datetime](https://github.com/Hacker0x01/react-datepicker) ^3.0.0. You should use this component
 * as a regular input but extended with a time/date picker. If you want to customize date picker behavior you can set every
 * datepicker's prop by passing `datePickerProps` object to this component. [Check this page](https://github.com/Hacker0x01/react-datepicker/blob/master/docs/datepicker.md)
 * for the full list of available datepicker's props. In order to register or set default locale please use registerLocale or setDefaultLocale methods imported from react-datepicker
 * and set as static methods on DateTimeInput component. This components set en-GB locale as a default.
 */
class DateTimeInput extends Component {
  static propTypes = {
    /** Function invoked on every date change: (instanceOfDateObject) => {} */
    onChange: PropTypes.func,
    /** Value of the input (instance of Date) */
    value: PropTypes.object,
    /** Custom input class name */
    className: PropTypes.string,
    /** List of props passed to datepicker component - [all props list](https://github.com/Hacker0x01/react-datepicker/blob/master/docs/datepicker.md) */
    datePickerProps: PropTypes.object,
    /** @ignore */
    placeholder: PropTypes.string,
    /** If true then reset icon will be displayed in the place of the original icon when value is set */
    isClearable: PropTypes.bool,
    /** Custom icon element to be displayed in the input */
    icon: PropTypes.node,
    /** If provided than [MaskedInput](/#maskedinput) component is used underneath. */
    mask: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    /** If true, renders an icon-only button instead of a text input */
    iconOnly: PropTypes.bool
  };
  static defaultProps = {
    value: undefined,
    className: undefined,
    datePickerProps: {},
    onChange: () => {},
    placeholder: undefined,
    icon: /*#__PURE__*/_jsx(Calendar, {}),
    isClearable: false,
    mask: undefined,
    iconOnly: false
  };
  state = {
    value: this.props.value ? this.props.value : undefined,
    mouseEntered: false
  };
  onMouseEnter = () => {
    if (this.state.mouseEntered === false) {
      this.setState(() => ({
        mouseEntered: true
      }));
    }
  };
  onMouseLeave = () => {
    if (this.state.mouseEntered === true) {
      this.setState(() => ({
        mouseEntered: false
      }));
    }
  };
  handleChange = date => {
    const {
      onChange
    } = this.props;
    onChange(date);
    this.setState({
      value: date || undefined
    });
  };
  onChangeRaw = date => {
    const {
      datePickerProps
    } = this.props;
    const {
      value
    } = date.target;
    if (datePickerProps.showTimeSelectOnly === true) {
      if (value === '') {
        this.handleChange(undefined);
      } else {
        const baseDate = this.state.value || new Date();
        const parsedDate = new Date(`${baseDate.toDateString()} ${value}`);
        // eslint-disable-next-line no-restricted-globals
        if (parsedDate instanceof Date && !isNaN(parsedDate)) {
          this.handleChange(parsedDate);
        }
      }
    } else {
      const newDate = parse(value, datePickerProps.dateFormat || 'MM/dd/yyyy', new Date());
      if (value && isValid(newDate)) {
        this.handleChange(newDate);
      }
    }
  };
  render() {
    const {
      className,
      onChange,
      datePickerProps,
      placeholder,
      onChangeFormat,
      isClearable,
      iconOnly,
      ...rest
    } = this.props; // eslint-disable-line
    const iconClasses = cln('icon', rest.icon.props.className, {
      'icon--clearable': this.state.value && isClearable
    });
    let icon;
    if (this.state.mouseEntered && this.state.value && isClearable) {
      icon = /*#__PURE__*/_jsx(ResetIcon, {
        ...rest.icon.props,
        "data-testid": "clear-icon",
        onClick: () => this.handleChange(undefined),
        className: iconClasses
      });
    } else {
      icon = /*#__PURE__*/_jsx(rest.icon.type, {
        ...rest.icon.props,
        className: iconClasses
      });
    }
    let InputContainer = Input;
    if (rest.mask) {
      InputContainer = MaskedInput;
    }
    return /*#__PURE__*/_jsx(DatePicker, {
      className: className,
      onChangeRaw: this.onChangeRaw,
      customInput: iconOnly ? /*#__PURE__*/_jsx(IconOnlyButton, {
        icon: icon
      }) : /*#__PURE__*/_jsx(InputContainer, {
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave,
        iconPosition: "right",
        placeholder: placeholder,
        ...rest,
        icon: icon
      }),
      selected: this.state.value,
      onChange: this.handleChange,
      popperClassName: cln('popper'),
      ...datePickerProps,
      isClearable: false,
      readOnly: iconOnly ? false : rest.readOnly,
      placeholderText: placeholder || datePickerProps.placeholderText
    });
  }
}
DateTimeInput.registerLocale = registerLocale;
DateTimeInput.setDefaultLocale = setDefaultLocale;
export default DateTimeInput;